import { TRANSACTION_CLS } from "../../../utils/constants";
import { ICmd, ICmdUtils } from "./ICmd";
import { ApplyFunction } from "../ApplyFunction";

interface TransactionMetaData {
  readonly api: string;
  readonly method: string;
  readonly encryption_key: string;
  readonly name: string;
  readonly url: string;
  readonly hd_path: string;
}

export const TransactionMetaDataUtils = {
  create({
    api,
    method,
    encryption_key,
    name,
    url,
    hd_path,
  }: {
    api: string;
    method: string;
    encryption_key: string;
    name: string;
    url: string;
    hd_path: string;
  }): TransactionMetaData {
    return {
      api,
      method,
      encryption_key,
      name,
      url,
      hd_path,
    };
  },

  empty(): TransactionMetaData {
    return {
      api: "",
      method: "",
      encryption_key: "",
      name: "Dapp",
      url: "",
      hd_path: "",
    };
  },

  fromMap(map: { [key: string]: any }): TransactionMetaData {
    return {
      api: map["api"] ?? "",
      method: map["method"] ?? "",
      encryption_key: map["encryption_key"] ?? "",
      name: map["name"] ?? "",
      url: map["url"] ?? "",
      hd_path: map["hd_path"] ?? "",
    };
  },

  toMap(metaData: TransactionMetaData): { [key: string]: any } {
    return {
      api: metaData.api,
      method: metaData.method,
      encryption_key: metaData.encryption_key,
      name: metaData.name,
      url: metaData.url,
      hd_path: metaData.hd_path,
    };
  },

  toJson(metaData: TransactionMetaData): string {
    return JSON.stringify(TransactionMetaDataUtils.toMap(metaData));
  },

  fromJson(json: string): TransactionMetaData {
    try {
      const map = JSON.parse(json);
      return TransactionMetaDataUtils.fromMap(map);
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return TransactionMetaDataUtils.empty();
    }
  },

  copyWith({
    api,
    method,
    encryption_key,
    name,
    url,
    hd_path,
  }: {
    api?: string;
    method?: string;
    encryption_key?: string;
    name?: string;
    url?: string;
    hd_path?: string;
  }): TransactionMetaData {
    return {
      api: api ?? "",
      method: method ?? "",
      encryption_key: encryption_key ?? "",
      name: name ?? "Dapp",
      url: url ?? "",
      hd_path: hd_path ?? "",
    };
  },
};

export interface TransactionMessage {
  readonly meta: TransactionMetaData;
  readonly data: any;
}

export const TransactionMessageUtils = {
  create({ meta, data }: { meta: TransactionMetaData; data: any }): TransactionMessage {
    return {
      meta,
      data,
    };
  },

  empty(): TransactionMessage {
    return {
      meta: TransactionMetaDataUtils.empty(),
      data: {},
    };
  },

  fromMap(map: { [key: string]: any }): TransactionMessage {
    return {
      meta: TransactionMetaDataUtils.fromMap(map["meta"] ?? {}),
      data: map["data"] ?? {},
    };
  },

  toMap(message: TransactionMessage): { [key: string]: any } {
    return {
      meta: TransactionMetaDataUtils.toMap(message.meta),
      data: message.data,
    };
  },

  toJson(message: TransactionMessage): string {
    return JSON.stringify(TransactionMessageUtils.toMap(message));
  },

  fromJson(json: string): TransactionMessage {
    try {
      const map = JSON.parse(json);
      return TransactionMessageUtils.fromMap(map);
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return TransactionMessageUtils.empty();
    }
  },
};

interface TransactionLink {
  readonly id: string;
  readonly safe_id: string;
  readonly link_parameter: string;
}

const TransactionLinkUtils = {
  create({
    id,
    safe_id,
    link_parameter = "transfer",
  }: {
    id: string;
    safe_id: string;
    link_parameter: string;
  }): TransactionLink {
    return {
      id,
      safe_id,
      link_parameter,
    };
  },

  empty(): TransactionLink {
    return {
      id: "",
      safe_id: "",
      link_parameter: "transfer",
    };
  },

  fromMap(map: { [key: string]: any }): TransactionLink {
    return {
      id: map["id"] ?? "",
      safe_id: map["safe_id"] ?? "",
      link_parameter: map["link_parameter"] ?? "transfer",
    };
  },

  toMap(link: TransactionLink): { [key: string]: any } {
    return {
      id: link.id,
      safe_id: link.safe_id,
      link_parameter: link.link_parameter,
    };
  },

  toJson(link: TransactionLink): string {
    return JSON.stringify(TransactionLinkUtils.toMap(link));
  },

  fromJson(json: string): TransactionLink {
    try {
      const map = JSON.parse(json);
      return TransactionLinkUtils.fromMap(map);
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return TransactionLinkUtils.empty();
    }
  },

  copyWith({
    id,
    safe_id,
    link_parameter,
  }: {
    id?: string;
    safe_id?: string;
    link_parameter?: string;
  }): TransactionLink {
    return {
      id: id ?? "",
      safe_id: safe_id ?? "",
      link_parameter: link_parameter ?? "transfer",
    };
  },
};

export interface Transaction extends ICmd {
  readonly address: string;
  readonly amount: number;
  readonly asset: string;
  readonly fee_rate: number;
  readonly is_main: boolean;
  readonly unit: string;
  readonly link: TransactionLink;
  readonly transactionMessage: TransactionMessage | null;
  readonly layer2data: Record<string, any> | null;
}

export const TransactionUtils = {
  create({
    id,
    parentGroup,
    execute = true,
    originalObjectSIG = "",
    SIGNATURE = "",
    comment = "",
    address,
    amount,
    asset,
    fee_rate,
    is_main,
    unit,
    transactionMessage,
    layer2data,
    applyFunction,
    safe_id,
  }: {
    id: string;
    parentGroup: string;
    execute: boolean;
    originalObjectSIG?: string;
    SIGNATURE?: string;
    comment?: string;
    safe_id: string;
    address: string;
    amount: number;
    asset: string;
    fee_rate: number;
    is_main: boolean;
    unit: string;
    transactionMessage?: TransactionMessage | null;
    layer2data?: Record<string, any> | null;
    applyFunction?: ApplyFunction;
  }): Transaction {
    return {
      ...ICmdUtils.create({
        cls: TRANSACTION_CLS,
        id: id,
        parentGroup: parentGroup,
        execute: execute,
        SIGNATURE: SIGNATURE,
        comment: comment ?? "",
        originalObjectSIG: originalObjectSIG,
      }),
      address: address,
      amount: amount,
      asset: asset,
      fee_rate: fee_rate,
      is_main: is_main,
      unit: unit,
      link: {
        ...TransactionLinkUtils.create({
          id: `${id}-link`,
          safe_id: safe_id,
          link_parameter: "transfer",
        }),
      },
      transactionMessage: transactionMessage ?? null,
      layer2data: layer2data ?? null,
      applyFunction: applyFunction ?? null,
    };
  },

  empty(): Transaction {
    return TransactionUtils.create({
      id: "",
      parentGroup: "",
      execute: true,
      address: "",
      amount: 0,
      asset: "Error",
      fee_rate: 0,
      is_main: false,
      unit: "",
      safe_id: "",
    });
  },

  fromMap(map: { [key: string]: any }): Transaction {
    try {
      return {
        cls: map["class"] ?? TRANSACTION_CLS,
        id: map["id"] ?? "",
        parentGroup: map["parentGroup"] ?? "",
        execute: map["execute"] ?? true,
        originalObjectSIG: map["originalObjectSIG"] ?? "",
        SIGNATURE: map["SIGNATURE"] ?? "",
        comment: map["comment"] ?? "",
        address: map["address"] ?? "",
        amount: map["amount"] ?? 0,
        asset: map["asset"] ?? "",
        fee_rate: map["fee_rate"] ?? 0,
        is_main: map["is_main"] ?? false,
        unit: map["unit"] ?? "",
        link: TransactionLinkUtils.fromMap(map["link"] || {}),
        transactionMessage: map["message"] ? TransactionMessageUtils.fromMap(map["message"]) : null,
        layer2data: map["layer2data"],
      };
    } catch (e) {
      console.error("Error parsing Transaction from map:", e);
      return TransactionUtils.empty();
    }
  },

  fromJson(json: string): Transaction {
    const map = JSON.parse(json);
    return TransactionUtils.fromMap(map);
  },

  toMap(transaction: Transaction): { [key: string]: any } {
    const superMap = ICmdUtils.toMap(transaction);
    return {
      ...superMap,
      unit: transaction.unit,
      is_main: transaction.is_main,
      link: TransactionLinkUtils.toMap(transaction.link),
      address: transaction.address,
      amount: transaction.amount.toString(),
      asset: transaction.asset.toString(),
      fee_rate: transaction.fee_rate.toString(),
      message: TransactionMessageUtils.toMap(
        transaction.transactionMessage || TransactionMessageUtils.empty()
      ),
      layer2data: transaction.layer2data || null,
    };
  },

  toJson(transaction: Transaction): string {
    return JSON.stringify(TransactionUtils.toMap(transaction));
  },
};
